import { isArray, orderBy, groupBy } from 'lodash'
import moment, { Moment } from 'moment'

import {
  API_DATE_FORMAT,
  DEFAULT_DATE_FORMAT,
  SIGN_UP_DATE_QUESTION,
  YEAR_FORMAT,
} from 'lib/participants/Constants'

const SUPPORTED_QUESTIONS = [
  'CheckBoxQuestion',
  'DropDownQuestion',
  'RadioButtonQuestion',
  'DateQuestion',
]

export const formatSignUpQuestion = (data: SignupFormQuestionResponse[]) => {
  const isNotExpectedData = !(data && isArray(data))

  if (isNotExpectedData) return []

  const supportedQuestions = data.filter(({ type }) =>
    SUPPORTED_QUESTIONS.includes(type),
  )

  const { false: normalQuestions, true: triggerQuestions } = groupBy(
    supportedQuestions,
    'hasTrigger',
  )

  let orderQuestionsBySequence = orderBy(normalQuestions, ['sequence'], ['asc'])

  if (triggerQuestions) {
    /**
     * ⚠️ trigger questions will be at last with random sequence.
     */
    orderQuestionsBySequence = [
      ...orderQuestionsBySequence,
      ...triggerQuestions,
    ]
  }

  return orderQuestionsBySequence as SignUpFilterQuestion[]
}

const getDateValueForQuery = ({
  date,
  yearOnlyDate,
  selected,
}: {
  date: Moment
  yearOnlyDate: boolean
  selected: string
}) => {
  if (!yearOnlyDate) return date.format(API_DATE_FORMAT)

  /**
   * ⚠️ For the "yearOnlyDate" the format should be for
   * Before: 31.12.SELECTED_YEAR
   * After: 01.01.SELECTED_YEAR
   */

  if (selected === 'before') {
    return `31.12.${date.format(YEAR_FORMAT)}`
  }

  return `01.01.${date.format(YEAR_FORMAT)}`
}

export const getRequestParams = (savedQuestions: SelectedSignUpQuestions[]) => {
  const requestParams = savedQuestions.map(
    ({ id, value, type, yearOnlyDate, selected }) => {
      if (type === SIGN_UP_DATE_QUESTION) {
        const selectedValue = value.length && value[0][selected]

        const queryDate = getDateValueForQuery({
          date: selectedValue,
          selected,
          yearOnlyDate,
        })

        return {
          condition: selected,
          field_name: 'signup_form',
          value: queryDate,
          question_id: id,
          option_ids: [],
        }
      }

      return {
        condition: 'matches set of',
        field_name: 'signup_form',
        question_id: id,
        option_ids: value.map((val) => val.id),
      }
    },
  )

  return requestParams
}

export const getDateQuestionSelectionFormat = ({
  question,
  value,
  id,
  type,
  yearOnlyDate,
}: SelectedSignUpQuestions) => {
  const currentValue = value[0]
  const hasValidAfterValue = currentValue.after?.isValid()
  const hasValidBeforeValue = currentValue.before?.isValid()
  const hasBothDatesFalsy = !hasValidAfterValue && !hasValidBeforeValue
  const hasBothDatesTruthy = hasValidAfterValue && hasValidBeforeValue

  if (hasBothDatesFalsy) return null

  const dateFormat = yearOnlyDate ? YEAR_FORMAT : DEFAULT_DATE_FORMAT

  const beforeDateFieldData = {
    id: id,
    question: question,
    value: [
      {
        ...currentValue,
        name: currentValue.before?.format(dateFormat),
      },
    ],
    type,
    yearOnlyDate,
    selected: 'before',
  }

  const afterDateFieldData = {
    id: id,
    question: question,
    value: [
      {
        ...currentValue,
        name: currentValue.after?.format(dateFormat),
      },
    ],
    type,
    yearOnlyDate,
    selected: 'after',
  }

  if (hasBothDatesTruthy) {
    return [beforeDateFieldData, afterDateFieldData]
  }

  if (hasValidAfterValue) return afterDateFieldData

  if (hasValidBeforeValue) return beforeDateFieldData
}

export const resetDateErrors = (questions: SignUpFilterQuestion[]) => {
  const updatedQuestions = questions.map((question) => {
    if (question.type === SIGN_UP_DATE_QUESTION) {
      if (!question.value) return question

      const currentValue = question.value[0]

      if (!currentValue) return question

      const hasValidBeforeDate = moment(currentValue.before).isValid()
      const hasValidAfterDate = moment(currentValue.after).isValid()

      const updatedValue = {
        before: hasValidBeforeDate ? currentValue.before : null,
        after: hasValidAfterDate ? currentValue.after : null,
      }

      return {
        ...question,
        value: [updatedValue],
      }
    }

    return question
  })

  return updatedQuestions
}
